import type {
  ModerationQueueResponse,
  ModerationSnapshotResponse,
  ResolveModerationResponse,
  AcknowledgeAutoRejectResponse,
  AutoRejectedQueueResponse,
  RejectionTemplatesResponse,
  PendingReviewFlagsResponse,
  ResolveFlaggedReviewResponse,
  ModeratorNotesResponse,
  CreateNoteResponse,
  SnapshotNotesResponse,
  UserBehaviorOverviewResponse,
  DefendantViolationHistoryResponse,
  ComprehensiveUserAnalysisResponse,
  ReviewModerationQueueResponse,
  AutoRejectedReviewsResponse,
  ResolveReviewModerationResponse,
  MaterialSearchResponse,
  MaterialContributionListItem,
  MaterialContributionRecord,
  GetAppealQueueResponse,
  RespondToAppealResponse,
  MigrationProgressRecord,
  PropagationStatusResponse,
  CraftRoomModerationQueueResponse,
  SubmitUnifiedFlagResponse,
  ResolveUnifiedFlagResponse,
  ResolveMaterialContributionApiResponse,
  RetryMigrationResponse,
  UpdateParentMaterialResponse,
  ListMigrationsResponse,
  MaterialSearchDocument,
  AcknowledgeMalwareTicketResponse,
  ModerationHistoryQuery,
  ModerationHistoryResponse,
  IncidentSeverity,
  ResolvePdfModerationRequest,
  ResolvePdfModerationResponse,
} from '@mrb91002/shared-types'
import { ModerationResolution } from '@mrb91002/shared-types'

import { secureFetch, SECURE_API_BASE } from './helpers'

// Pattern Moderation
export async function fetchModerationQueue(idToken: string): Promise<ModerationQueueResponse> {
  return secureFetch<ModerationQueueResponse>(`${SECURE_API_BASE}/moderation/unresolved/needs-review`, idToken, {
    method: 'GET',
  })
}

export async function fetchModerationSnapshot(
  snapshotId: string,
  idToken: string,
): Promise<ModerationSnapshotResponse> {
  return secureFetch<ModerationSnapshotResponse>(`${SECURE_API_BASE}/moderation/snapshot/${encodeURIComponent(snapshotId)}`, idToken, {
    method: 'GET',
  })
}

export async function resolveModerationItem(
  snapshotId: string,
  resolution: ModerationResolution.APPROVED | ModerationResolution.REJECTED,
  idToken: string,
  notes?: string,
  userMessage?: string,
  severity?: IncidentSeverity,
): Promise<ResolveModerationResponse> {
  return secureFetch<ResolveModerationResponse>(`${SECURE_API_BASE}/moderation/snapshot/${encodeURIComponent(snapshotId)}/resolve`, idToken, {
    method: 'POST',
    body: JSON.stringify({ resolution, notes, message: userMessage, severity }),
  })
}

export async function acknowledgeAutoReject(
  snapshotId: string,
  decision: 'correct' | 'incorrect',
  idToken: string,
  notes?: string,
  severity?: IncidentSeverity,
): Promise<AcknowledgeAutoRejectResponse> {
  return secureFetch<AcknowledgeAutoRejectResponse>(`${SECURE_API_BASE}/moderation/snapshot/${encodeURIComponent(snapshotId)}/acknowledge`, idToken, {
    method: 'POST',
    body: JSON.stringify({ decision, notes, severity }),
  })
}

export async function acknowledgeMalwareTicket(
  userId: string,
  body: { ticket_sk?: string; created_at?: string; pattern_id?: string; pdf_hash?: string; notes?: string },
  idToken: string,
): Promise<AcknowledgeMalwareTicketResponse> {
  return secureFetch<AcknowledgeMalwareTicketResponse>(
    `${SECURE_API_BASE}/moderation/malware/${encodeURIComponent(userId)}/acknowledge`,
    idToken,
    {
      method: 'POST',
      body: JSON.stringify(body),
    },
  )
}

export async function fetchAutoRejectedQueue(
  idToken: string,
  reviewed: boolean = false,
): Promise<AutoRejectedQueueResponse> {
  const url = reviewed
    ? `${SECURE_API_BASE}/moderation/auto-rejected?reviewed=true`
    : `${SECURE_API_BASE}/moderation/unresolved/auto-rejected`
  return secureFetch<AutoRejectedQueueResponse>(url, idToken, { method: 'GET' })
}

export async function fetchRejectionTemplates(idToken: string): Promise<RejectionTemplatesResponse> {
  return secureFetch<RejectionTemplatesResponse>(`${SECURE_API_BASE}/moderation/rejection-templates`, idToken, {
    method: 'GET',
  })
}

export async function fetchPendingReviewFlags(idToken: string): Promise<PendingReviewFlagsResponse> {
  return secureFetch<PendingReviewFlagsResponse>(`${SECURE_API_BASE}/moderation/unresolved/flags`, idToken, {
    method: 'GET',
  })
}

// Material Contributions
export async function fetchMaterialContributions(
  idToken: string,
): Promise<{ items: MaterialContributionListItem[]; count: number }> {
  return secureFetch(`${SECURE_API_BASE}/moderation/material-contributions`, idToken, {
    method: 'GET',
  })
}

export async function getMaterialContribution(
  contributionId: string,
  idToken: string,
): Promise<MaterialContributionRecord> {
  return secureFetch<MaterialContributionRecord>(
    `${SECURE_API_BASE}/moderation/material-contributions/${encodeURIComponent(contributionId)}`,
    idToken,
    { method: 'GET' },
  )
}

export async function resolveMaterialContribution(
  contributionId: string,
  resolution: ModerationResolution.APPROVED | ModerationResolution.REJECTED,
  idToken: string,
  notes?: string,
): Promise<ResolveMaterialContributionApiResponse> {
  return secureFetch<ResolveMaterialContributionApiResponse>(`${SECURE_API_BASE}/moderation/material-contributions/${contributionId}/resolve`, idToken, {
    method: 'POST',
    body: JSON.stringify({ resolution, notes }),
  })
}

export async function setVariationRequest(
  contributionId: string,
  data: {
    parent_material_id: string
    variation_name: string
  },
  idToken: string,
): Promise<void> {
  return secureFetch<void>(
    `${SECURE_API_BASE}/moderation/material-contributions/${contributionId}/set-variation-request`,
    idToken,
    {
      method: 'POST',
      body: JSON.stringify(data),
    },
  )
}

export async function createParentWithVariation(
  contributionId: string,
  parentData: Record<string, unknown>,
  idToken: string,
): Promise<void> {
  return secureFetch<void>(
    `${SECURE_API_BASE}/moderation/material-contributions/${contributionId}/create-parent-with-variation`,
    idToken,
    {
      method: 'POST',
      body: JSON.stringify(parentData),
    },
  )
}

export async function saveContributionOverrides(
  contributionId: string,
  materialData: Record<string, unknown>,
  idToken: string,
): Promise<void> {
  return secureFetch<void>(
    `${SECURE_API_BASE}/moderation/material-contributions/${contributionId}/overrides`,
    idToken,
    {
      method: 'PATCH',
      body: JSON.stringify({ material_data: materialData }),
    },
  )
}

export async function searchMaterialsForModeration(query: string, idToken: string): Promise<MaterialSearchResponse> {
  return secureFetch(`${SECURE_API_BASE}/materials/search?q=${encodeURIComponent(query)}`, idToken, { method: 'GET' })
}

// PDF Content Moderation
export async function resolvePdfModeration(
  patternId: string,
  body: ResolvePdfModerationRequest,
  idToken: string,
): Promise<ResolvePdfModerationResponse> {
  return secureFetch<ResolvePdfModerationResponse>(
    `${SECURE_API_BASE}/moderation/pdf/${encodeURIComponent(patternId)}/resolve`,
    idToken,
    {
      method: 'POST',
      body: JSON.stringify(body),
    },
  )
}

export async function resolveFlaggedReview(
  flagId: string,
  outcome: 'dismissed' | 'approved',
  idToken: string,
  notes?: string,
): Promise<ResolveFlaggedReviewResponse> {
  return secureFetch<ResolveFlaggedReviewResponse>(`${SECURE_API_BASE}/moderation/flag/${encodeURIComponent(flagId)}/resolve`, idToken, {
    method: 'POST',
    body: JSON.stringify({ outcome, notes }),
  })
}

// Moderator Notes
export async function fetchModeratorNotes(userId: string, idToken: string): Promise<ModeratorNotesResponse> {
  return secureFetch<ModeratorNotesResponse>(`${SECURE_API_BASE}/moderation/user/${encodeURIComponent(userId)}/notes`, idToken, {
    method: 'GET',
  })
}

export async function createUserNote(
  userId: string,
  content: string,
  idToken: string,
  patternId?: string,
  category: string = 'other',
  severity: string = 'medium',
): Promise<CreateNoteResponse> {
  return secureFetch<CreateNoteResponse>(`${SECURE_API_BASE}/moderation/user/${encodeURIComponent(userId)}/notes`, idToken, {
    method: 'POST',
    body: JSON.stringify({ content, patternId, category, severity }),
  })
}

export async function fetchSnapshotNotes(snapshotId: string, idToken: string): Promise<SnapshotNotesResponse> {
  return secureFetch<SnapshotNotesResponse>(`${SECURE_API_BASE}/moderation/snapshot/${encodeURIComponent(snapshotId)}/notes`, idToken, {
    method: 'GET',
  })
}

export async function createSnapshotNote(
  snapshotId: string,
  content: string,
  idToken: string,
  category: string = 'other',
  severity: string = 'medium',
): Promise<CreateNoteResponse> {
  return secureFetch<CreateNoteResponse>(`${SECURE_API_BASE}/moderation/snapshot/${encodeURIComponent(snapshotId)}/notes`, idToken, {
    method: 'POST',
    body: JSON.stringify({ content, category, severity }),
  })
}

// User Behavior Analysis
export async function fetchUserBehaviorOverview(
  userId: string,
  idToken: string,
): Promise<UserBehaviorOverviewResponse> {
  return secureFetch<UserBehaviorOverviewResponse>(`${SECURE_API_BASE}/moderation/user/${encodeURIComponent(userId)}/behavior-overview`, idToken, {
    method: 'GET',
  })
}

export async function fetchDefendantViolationHistory(
  userId: string,
  idToken: string,
): Promise<DefendantViolationHistoryResponse> {
  return secureFetch(`${SECURE_API_BASE}/moderation/user/${encodeURIComponent(userId)}/violation-history`, idToken, {
    method: 'GET',
  })
}

export async function fetchComprehensiveUserAnalysis(
  userId: string,
  filters: {
    activityTypes?: string[]
    timeRange?: string
    riskLevels?: string[]
    search?: string
    limit?: number
    offset?: number
  },
  idToken: string,
): Promise<ComprehensiveUserAnalysisResponse> {
  const params = new URLSearchParams({
    limit: (filters.limit || 20).toString(),
    offset: (filters.offset || 0).toString(),
    time_range: filters.timeRange || 'all',
  })

  if (filters.activityTypes && filters.activityTypes.length > 0) {
    params.set('activity_types', filters.activityTypes.join(','))
  }
  if (filters.riskLevels && filters.riskLevels.length > 0) {
    params.set('risk_levels', filters.riskLevels.join(','))
  }
  if (filters.search && filters.search.trim()) {
    params.set('search', filters.search.trim())
  }

  return secureFetch<ComprehensiveUserAnalysisResponse>(
    `${SECURE_API_BASE}/moderation/user/${encodeURIComponent(userId)}/comprehensive-analysis?${params}`,
    idToken,
    { method: 'GET' },
  )
}

// Moderation History
export async function getModerationHistory(
  params: ModerationHistoryQuery,
  idToken: string,
): Promise<ModerationHistoryResponse> {
  const search = new URLSearchParams()
  if (params.user_id) search.set('user_id', params.user_id)
  if (params.screen_name) search.set('screen_name', params.screen_name)
  if (params.start_date) search.set('start_date', params.start_date)
  if (params.end_date) search.set('end_date', params.end_date)
  if (params.content_type) search.set('content_type', params.content_type)
  if (params.resolution) search.set('resolution', params.resolution)
  if (params.page !== undefined) search.set('page', params.page.toString())
  if (params.page_size !== undefined) search.set('page_size', params.page_size.toString())

  return secureFetch<ModerationHistoryResponse>(
    `${SECURE_API_BASE}/moderation/history?${search}`,
    idToken,
    { method: 'GET' },
  )
}

// Review Moderation
export async function fetchReviewModerationQueue(idToken: string): Promise<ReviewModerationQueueResponse> {
  return secureFetch<ReviewModerationQueueResponse>(`${SECURE_API_BASE}/moderation/unresolved/review-moderations`, idToken, {
    method: 'GET',
  })
}

export async function fetchAutoRejectedReviews(idToken: string): Promise<AutoRejectedReviewsResponse> {
  return secureFetch<AutoRejectedReviewsResponse>(`${SECURE_API_BASE}/moderation/auto-rejected-reviews`, idToken, {
    method: 'GET',
  })
}

export async function approveReview(
  moderationId: string,
  idToken: string,
  notes?: string,
): Promise<ResolveReviewModerationResponse> {
  return secureFetch<ResolveReviewModerationResponse>(
    `${SECURE_API_BASE}/moderation/review-moderation/${encodeURIComponent(moderationId)}/resolve`,
    idToken,
    {
      method: 'POST',
      body: JSON.stringify({ action: 'approve', message: notes }),
    },
  )
}

export async function rejectReview(
  moderationId: string,
  idToken: string,
  notes?: string,
  userMessage?: string,
): Promise<ResolveReviewModerationResponse> {
  return secureFetch<ResolveReviewModerationResponse>(
    `${SECURE_API_BASE}/moderation/review-moderation/${encodeURIComponent(moderationId)}/resolve`,
    idToken,
    {
      method: 'POST',
      body: JSON.stringify({ action: 'reject', message: userMessage || notes }),
    },
  )
}

// Craft Room Moderation
export async function fetchCraftRoomModerationQueue(
  queueType: 'needs_review' | 'auto_rejected',
  idToken: string,
): Promise<CraftRoomModerationQueueResponse> {
  return secureFetch<CraftRoomModerationQueueResponse>(`${SECURE_API_BASE}/moderation/craft-room/unresolved/${queueType}`, idToken, {
    method: 'GET',
  })
}

export async function resolveCraftRoomModeration(
  pk: string,
  sk: string,
  resolution: ModerationResolution.APPROVED | ModerationResolution.REJECTED | ModerationResolution.ACKNOWLEDGED,
  notes: string | undefined,
  idToken: string,
): Promise<void> {
  return secureFetch<void>(`${SECURE_API_BASE}/moderation/craft-room/resolve`, idToken, {
    method: 'POST',
    body: JSON.stringify({ PK: pk, SK: sk, resolution, notes }),
  })
}

// Flags & Appeals
export async function submitUnifiedFlag(
  contentType: 'comment' | 'pattern' | 'project' | 'material' | 'profile' | 'review',
  contentId: string,
  reason: string,
  comment: string | undefined,
  context: Record<string, unknown> | undefined,
  idToken: string,
): Promise<SubmitUnifiedFlagResponse> {
  return secureFetch<SubmitUnifiedFlagResponse>(`${SECURE_API_BASE}/flag`, idToken, {
    method: 'POST',
    body: JSON.stringify({ content_type: contentType, content_id: contentId, reason, comment, context }),
  })
}

export async function resolveUnifiedFlag(
  flagId: string,
  outcome: 'dismissed' | 'approved',
  notes: string | undefined,
  idToken: string,
  options?: {
    severity?: IncidentSeverity
    hard_delete?: boolean
    edits?: Record<string, unknown>
    revert_to_version?: number
    clear_fields?: ('bio' | 'profile_image' | 'website' | 'etsy_link')[]
  },
): Promise<ResolveUnifiedFlagResponse> {
  return secureFetch<ResolveUnifiedFlagResponse>(`${SECURE_API_BASE}/moderation/flags/${encodeURIComponent(flagId)}/resolve`, idToken, {
    method: 'POST',
    body: JSON.stringify({ outcome, notes, ...options }),
  })
}

export async function getAppealQueue(idToken: string): Promise<GetAppealQueueResponse> {
  return secureFetch(`${SECURE_API_BASE}/moderation/appeals`, idToken, {
    method: 'GET',
  })
}

export async function respondToAppeal(
  appealId: string,
  message: string,
  idToken: string,
  resolution?: 'approved' | 'rejected',
): Promise<RespondToAppealResponse> {
  return secureFetch(`${SECURE_API_BASE}/moderation/appeals/${appealId}/respond`, idToken, {
    method: 'POST',
    body: JSON.stringify({ message, resolution }),
  })
}

// Migration Progress
export async function fetchMigrationProgress(
  contributionId: string,
  idToken: string,
): Promise<{ progress: MigrationProgressRecord }> {
  return secureFetch(`${SECURE_API_BASE}/moderation/migration-progress/${contributionId}`, idToken, {
    method: 'GET',
  })
}

export async function retryFailedMigration(
  contributionId: string,
  idToken: string,
): Promise<RetryMigrationResponse> {
  return secureFetch<RetryMigrationResponse>(
    `${SECURE_API_BASE}/moderation/material-contributions/${contributionId}/retry-migration`,
    idToken,
    { method: 'POST' },
  )
}

export async function listAllMigrations(
  idToken: string,
  filters?: { status?: string; limit?: number; lastKey?: string },
): Promise<ListMigrationsResponse> {
  const params = new URLSearchParams()
  if (filters?.status) params.set('status', filters.status)
  if (filters?.limit) params.set('limit', filters.limit.toString())
  if (filters?.lastKey) params.set('lastKey', filters.lastKey)

  return secureFetch<ListMigrationsResponse>(`${SECURE_API_BASE}/moderation/migration-progress?${params}`, idToken, { method: 'GET' })
}

export async function updateParentMaterial(
  parentMaterialId: string,
  fields: Record<string, unknown>,
  idToken: string,
): Promise<UpdateParentMaterialResponse> {
  return secureFetch<UpdateParentMaterialResponse>(`${SECURE_API_BASE}/moderation/parent-material/${parentMaterialId}`, idToken, {
    method: 'PATCH',
    body: JSON.stringify({ fields }),
  })
}

export async function getPropagationStatus(
  propagationId: string,
  idToken: string,
): Promise<PropagationStatusResponse> {
  return secureFetch(`${SECURE_API_BASE}/moderation/propagation/${propagationId}`, idToken, { method: 'GET' })
}

// ===== PURCHASE LINKS MANAGEMENT =====

export async function fetchMaterialsWithoutAmazonOffers(
  idToken: string,
  page: number = 1,
  limit: number = 20,
): Promise<{ items: MaterialSearchDocument[]; total: number; page: number }> {
  return secureFetch<{ items: MaterialSearchDocument[]; total: number; page: number }>(
    `${SECURE_API_BASE}/materials/without-amazon-offers?page=${page}&limit=${limit}`,
    idToken,
    { method: 'GET' },
  )
}

export async function saveOffers(
  materialId: string,
  offers: { amazon_url?: string; etsy_url?: string; other_urls?: Array<{ label: string; url: string }> },
  idToken: string,
): Promise<unknown> {
  return secureFetch(
    `${SECURE_API_BASE}/materials/${encodeURIComponent(materialId)}/offers`,
    idToken,
    { method: 'PUT', body: JSON.stringify(offers) },
  )
}
